import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, RefreshCw, AlertTriangle, CheckCircle2, Info, PlugZap, PartyPopper } from "lucide-react";

const NOTE_STYLE = {
  ok: ["text-emerald-300 border-emerald-500/30 bg-emerald-500/5", CheckCircle2],
  warn: ["text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/5", AlertTriangle],
  info: ["text-[#44403C] border-[#E7DFD5] bg-black/5", Info],
};

const PayoutNote = ({ note }) => {
  if (!note) return null;
  const [cls, Icon] = NOTE_STYLE[note.tone] || NOTE_STYLE.info;
  return (
    <div className={`mt-4 flex items-start gap-2 border px-3 py-2 text-xs leading-relaxed max-w-2xl ${cls}`} data-testid="stripe-payout-note" data-tone={note.tone}>
      <Icon size={14} className="shrink-0 mt-0.5" /> <span>{note.text}</span>
    </div>
  );
};

const FirstSaleBanner = ({ sale }) => {
  if (!sale) return null;
  const when = sale.at ? new Date(sale.at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
  return (
    <div className="mb-4 flex items-start gap-2.5 border border-[#F59E0B]/40 bg-[#F59E0B]/[0.07] px-3.5 py-3 text-xs leading-relaxed max-w-2xl text-[#B45309]" data-testid="stripe-first-sale">
      <PartyPopper size={15} className="shrink-0 mt-0.5" />
      <div>
        <div className="font-mono text-[9px] tracking-[0.2em] uppercase mb-1">§ First sale — a milestone</div>
        <span className="text-[#44403C]">
          Your first real sale landed{sale.product ? <> — <strong>{sale.product}</strong></> : null}
          {sale.amount ? <> for <strong>{sale.amount}</strong></> : null}{when ? <> on {when}</> : null}. This one you never forget.
        </span>
      </div>
    </div>
  );
};

const WiringVerdict = ({ wiring }) => {
  if (!wiring) return null;
  const [cls, Icon] = NOTE_STYLE[wiring.tone] || NOTE_STYLE.info;
  return (
    <div className={`mb-4 flex items-start gap-2.5 border px-3.5 py-3 text-xs leading-relaxed max-w-2xl ${cls}`} data-testid="stripe-wiring" data-tone={wiring.tone} data-connected={String(!!wiring.connected)}>
      <Icon size={15} className="shrink-0 mt-0.5" />
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] uppercase">
            <PlugZap size={11} /> Wiring audit
          </span>
          {wiring.mode && <span className="font-mono text-[9px] tracking-[0.2em] uppercase opacity-70" data-testid="stripe-wiring-mode">{wiring.mode} mode</span>}
          {wiring.account_last4 && <span className="font-mono text-[9px] tracking-[0.2em] uppercase opacity-70">acct …{wiring.account_last4}</span>}
          {wiring.business_name && <span className="font-mono text-[9px] tracking-[0.2em] uppercase opacity-70">{wiring.business_name}</span>}
        </div>
        <span data-testid="stripe-wiring-text">{wiring.text}</span>
      </div>
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const StripeTruth = ({ stats, onReconciled }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/admin/stripe/summary`).then((r) => setS(r.data)).catch(() => setS({ ok: false })); }, []);

  const reconcile = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/orders/reconcile`);
      const d = r.data;
      setS(d.stripe);
      toast.success(`Checked ${d.checked} unpaid checkout${d.checked === 1 ? "" : "s"} with Stripe.`, {
        description: `${d.marked_paid} newly paid · ${d.marked_abandoned} abandoned${d.not_in_stripe ? ` (${d.not_in_stripe} old test checkouts Stripe doesn't know)` : ""} · ${d.still_open} still open${d.unknown ? ` · ${d.unknown} unknown` : ""}`,
        duration: 9000,
      });
      onReconciled?.(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reach Stripe.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tacc-card p-5 md:p-6 mb-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-testid="stripe-truth">
      <div className="flex-1">
        <div className="section-tag mb-2 flex items-center gap-2"><Landmark size={12} /> § What Stripe actually has</div>
        {s && s.ok && <FirstSaleBanner sale={s.first_sale} />}
        {s && s.ok && <WiringVerdict wiring={s.wiring} />}
        {s === null ? (
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] animate-pulse">Asking Stripe…</div>
        ) : s.ok ? (
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <div><div className="font-display text-2xl text-emerald-400" data-testid="stripe-paid">{s.paid_charges}</div><div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C]">payments taken</div></div>
            <div><div className="font-display text-2xl text-[#1C1917]" data-testid="stripe-gross">{money(s.gross)}</div><div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C]">gross{s.refunded ? ` · ${money(s.refunded)} refunded` : ""}</div></div>
            <div><div className="font-display text-2xl text-[#B45309]" data-testid="stripe-balance">{money(s.balance_available)}</div><div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C]">available · {money(s.balance_pending)} pending payout</div></div>
          </div>
        ) : (
          <div className="text-sm text-red-300" data-testid="stripe-error">Stripe didn't answer{s.error ? ` — ${s.error}` : ""}.</div>
        )}
        {s && <PayoutNote note={s.payout_note} />}
        <p className="text-xs text-[#A8A29E] mt-3 max-w-2xl" data-testid="orders-explainer">
          A checkout is counted as an <span className="text-[#44403C]">order</span> only when Stripe confirms the card was charged. "Checkouts started" are people (and our own tests) who opened the Stripe page — most never pay. {stats?.open_checkouts ? `${stats.open_checkouts} open · ` : ""}{stats?.abandoned_checkouts ? `${stats.abandoned_checkouts} abandoned.` : ""}
        </p>
      </div>
      <button onClick={reconcile} disabled={busy} className="btn-outline text-xs whitespace-nowrap disabled:opacity-50" data-testid="orders-reconcile-btn" title="Ask Stripe about every unpaid checkout: paid ones get unlocked and receipted, expired ones are marked abandoned">
        <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {busy ? "Checking with Stripe…" : "Reconcile with Stripe"}
      </button>
    </div>
  );
};
